// mui
import {
  Alert,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

// models
import type { ChatSessionMode, ConversationConfig } from '@karehero/models';
import { formatDateTime } from '@karehero/helpers/datetime';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';

/** A chat session of a sandbox conversation, as listed to open again. */
export interface SandboxSession {
  id: string;
  startedAt: string;
  messageCount: number;
}

/** A sandbox conversation, with the sessions started of it, latest first. */
export interface SandboxConversation {
  config: ConversationConfig;
  sessions: SandboxSession[];
}

export interface SandboxPageProps {
  conversations: SandboxConversation[];
  /** The conversation a new session is being started of, if any. */
  startingMode?: ChatSessionMode;
  isLoading?: boolean;
  isError?: boolean;
  onStartConversation: (mode: ChatSessionMode) => void;
  onOpenSession: (mode: ChatSessionMode, sessionId: string) => void;
  onBack: () => void;
}

const messageCountLabel = (count: number) =>
  count === 1 ? '1 message' : `${count} messages`;

/**
 * SandboxPage lists the conversations for trying the chat engine out: each can
 * be started anew, or any session started of it opened again.
 */
export const SandboxPage = ({
  conversations,
  startingMode,
  isLoading = false,
  isError = false,
  onStartConversation,
  onOpenSession,
  onBack,
}: SandboxPageProps) => {
  const content = isLoading ? (
    <Stack sx={{ alignItems: 'center', py: 4 }}>
      <CircularProgress size={32} />
    </Stack>
  ) : isError ? (
    <Alert severity='error'>
      We couldn't load the sandbox conversations. Please try again.
    </Alert>
  ) : conversations.length === 0 ? (
    <Typography variant='body1' color='text.secondary'>
      There are no sandbox conversations yet.
    </Typography>
  ) : (
    <Stack spacing={4}>
      {conversations.map(({ config, sessions }) => (
        <Stack key={config.mode} spacing={1}>
          <Stack
            direction='row'
            spacing={2}
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Stack>
              <Typography variant='h6'>{config.title}</Typography>
              <Typography variant='body2' color='text.secondary'>
                {config.description}
              </Typography>
            </Stack>
            <Button
              variant='outlined'
              size='small'
              startIcon={<Icon icon='plus' size='sm' />}
              loading={startingMode === config.mode}
              disabled={!!startingMode}
              onClick={() => onStartConversation(config.mode)}
            >
              New
            </Button>
          </Stack>
          {sessions.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              None started yet.
            </Typography>
          ) : (
            <List disablePadding>
              {sessions.map((session) => (
                <ListItemButton
                  key={session.id}
                  divider
                  onClick={() => onOpenSession(config.mode, session.id)}
                >
                  <ListItemText
                    primary={`Started ${formatDateTime(session.startedAt)}`}
                    secondary={messageCountLabel(session.messageCount)}
                  />
                  <Icon icon='chevron-right' size='sm' />
                </ListItemButton>
              ))}
            </List>
          )}
        </Stack>
      ))}
    </Stack>
  );

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        headerSlot={
          <PageHeader
            title='Sandbox'
            description='Conversations for trying the chat engine out. Never shown in production.'
          />
        }
        contentSlot={content}
      />
    </ModalPageTemplate>
  );
};

SandboxPage.displayName = 'SandboxPage';
export default SandboxPage;
